import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, GraduationCap, User, Award, RotateCcw } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { t } from "../lib/i18n";

function slideText(slide, lang) {
    if (lang === "hi" && slide.text_hi) return slide.text_hi;
    if (lang === "bn" && slide.text_bn) return slide.text_bn;
    return slide.text;
}
function questionText(q, lang) {
    if (lang === "hi" && q.q_text_hi) return q.q_text_hi;
    if (lang === "bn" && q.q_text_bn) return q.q_text_bn;
    return q.q_text;
}
function optionText(q, i, lang) {
    if (lang === "hi" && q.options_hi?.[i]) return q.options_hi[i];
    if (lang === "bn" && q.options_bn?.[i]) return q.options_bn[i];
    return q.options[i];
}

function ytEmbed(url) {
    if (!url) return null;
    const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{11})/);
    return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

export default function CourseTakePage() {
    const { user } = useAuth();
    const { courseId } = useParams();
    const [course, setCourse] = useState(null);
    const [sites, setSites] = useState([]);
    const [siteId, setSiteId] = useState("");
    const [roster, setRoster] = useState([]);
    const [staffId, setStaffId] = useState("");
    const [lang, setLang] = useState(user?.language || "en");
    const [phase, setPhase] = useState("setup"); // setup | slides | quiz | result
    const [slideIdx, setSlideIdx] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [result, setResult] = useState(null);

    useEffect(() => {
        api.get(`/courses/${courseId}`).then(({ data }) => setCourse(data)).catch((e) => toast.error(apiError(e)));
        api.get("/sites").then(({ data }) => { setSites(data); if (data.length) setSiteId(data[0].id); }).catch(() => {});
    }, [courseId]);

    useEffect(() => {
        if (!siteId) { setRoster([]); return; }
        api.get(`/sites/${siteId}/roster`).then(({ data }) => setRoster(data || [])).catch(() => setRoster([]));
    }, [siteId]);

    function startForStaff() {
        setAnswers(new Array(course.quiz.length).fill(-1));
        setSlideIdx(0);
        setPhase("slides");
    }

    function startSelfTake() {
        setStaffId("");
        setAnswers(new Array(course.quiz.length).fill(-1));
        setSlideIdx(0);
        setPhase("slides");
    }

    async function submitQuiz() {
        if (answers.some((a) => a < 0)) { toast.error(t(user, "training_answer_all")); return; }
        try {
            const payload = { course_id: course.id, staff_id: staffId || null, site_id: staffId ? siteId : null, answers };
            const { data } = await api.post("/courses/attempt", payload);
            setResult(data);
            setPhase("result");
        } catch (e) { toast.error(apiError(e)); }
    }

    if (!course) return <div className="mx-auto max-w-3xl p-6"><div className="h-64 bg-white rounded-xl animate-pulse" /></div>;

    const slides = course.slides || [];
    const quiz = course.quiz || [];

    return (
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-4 sm:py-8">
            <Link to="/training" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#1E3355] mb-3"><ChevronLeft className="w-4 h-4" /> {t(user, "training_all_courses")}</Link>

            {phase === "setup" && (
                <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-5" data-testid="take-setup">
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">{t(user, "training_start")}</div>
                    <h1 className="font-display text-2xl font-bold text-[#1E3355] mt-1 flex items-center gap-2"><GraduationCap className="w-6 h-6" /> {course.title}</h1>
                    {course.description && <p className="text-sm text-gray-600 mt-1">{course.description}</p>}
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t(user, "training_lang")}</label>
                            <select value={lang} onChange={(e) => setLang(e.target.value)} className={fieldCls} data-testid="take-lang">
                                <option value="en">English</option><option value="hi">हिन्दी</option><option value="bn">বাংলা</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t(user, "training_site")}</label>
                            <select value={siteId} onChange={(e) => setSiteId(e.target.value)} className={fieldCls} data-testid="take-site">
                                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="mt-4">
                        <div className="text-sm font-medium text-gray-700 mb-2">{t(user, "training_pick_staff")}</div>
                        {roster.length === 0 ? (
                            <div className="text-xs text-gray-500">No roster staff at this site.</div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {roster.map((s) => (
                                    <button key={s.id} onClick={() => { setStaffId(s.id); startForStaff(); }} className={`text-left p-3 rounded-lg border ${staffId === s.id ? "border-[#1E3355] bg-[#F4F6F9]" : "border-gray-200 hover:border-[#1E3355]"}`} data-testid={`take-staff-${s.id}`}>
                                        <div className="font-semibold text-sm text-[#1E3355]">{s.name}</div>
                                        <div className="text-[11px] text-gray-500">{s.role}</div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                        <div className="text-xs text-gray-500">{t(user, "training_or_self")}</div>
                        <button onClick={startSelfTake} className="text-sm font-semibold text-[#1E3355] hover:underline" data-testid="take-self-btn">{t(user, "training_self_take")}</button>
                    </div>
                </div>
            )}

            {phase === "slides" && (
                <SlideView slide={slides[slideIdx]} lang={lang} idx={slideIdx} total={slides.length}
                    onPrev={() => slideIdx > 0 && setSlideIdx(slideIdx - 1)}
                    onNext={() => {
                        if (slideIdx < slides.length - 1) setSlideIdx(slideIdx + 1);
                        else setPhase("quiz");
                    }}
                    user={user} />
            )}

            {phase === "quiz" && (
                <QuizView quiz={quiz} lang={lang} answers={answers} setAnswers={setAnswers} onSubmit={submitQuiz} user={user} />
            )}

            {phase === "result" && result && (
                <ResultCard result={result} staffName={roster.find((r) => r.id === staffId)?.name} onRetry={() => { setPhase("setup"); setResult(null); }} user={user} />
            )}
        </div>
    );
}

function SlideView({ slide, lang, idx, total, onPrev, onNext, user }) {
    const yt = ytEmbed(slide.youtube_url);
    return (
        <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-hidden" data-testid={`slide-view-${idx}`}>
            <div className="p-4 border-b border-gray-100">
                <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">{t(user, "training_slide")} {idx + 1} / {total}</div>
                <div className="h-1.5 bg-gray-100 rounded-full mt-2 overflow-hidden">
                    <div className="h-full bg-[#1E3355]" style={{ width: `${((idx + 1) / total) * 100}%` }} />
                </div>
            </div>
            <div className="p-6 min-h-[220px]">
                <p className="text-base leading-relaxed text-gray-800" data-testid="slide-text">{slideText(slide, lang)}</p>
                {yt && <div className="mt-4 aspect-video"><iframe src={yt} className="w-full h-full rounded-md" allowFullScreen title="video" /></div>}
            </div>
            <div className="flex border-t border-gray-100">
                <button onClick={onPrev} disabled={idx === 0} className="flex-1 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40" data-testid="slide-prev"><ChevronLeft className="w-4 h-4 inline" /> {t(user, "back")}</button>
                <button onClick={onNext} className="flex-1 py-3 text-sm font-semibold text-white bg-[#1E3355] hover:bg-[#15243C]" data-testid="slide-next">{idx === total - 1 ? t(user, "training_start_quiz") : t(user, "next")} <ChevronRight className="w-4 h-4 inline" /></button>
            </div>
        </div>
    );
}

function QuizView({ quiz, lang, answers, setAnswers, onSubmit, user }) {
    return (
        <div className="space-y-3" data-testid="quiz-view">
            {quiz.map((q, i) => (
                <div key={i} className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-4" data-testid={`quiz-q-${i}`}>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">{t(user, "training_question")} {i + 1}</div>
                    <div className="font-display font-semibold text-[#1E3355]">{questionText(q, lang)}</div>
                    <div className="mt-3 space-y-1.5">
                        {q.options.map((opt, k) => {
                            const sel = answers[i] === k;
                            return (
                                <button key={k} onClick={() => setAnswers(answers.map((a, j) => j === i ? k : a))} className={`w-full text-left p-2.5 rounded-md text-sm border transition-colors ${sel ? "bg-[#1E3355] text-white border-[#1E3355]" : "bg-white text-gray-700 border-gray-200 hover:border-[#1E3355]"}`} data-testid={`quiz-${i}-opt-${k}`}>
                                    {String.fromCharCode(65 + k)}. {optionText(q, k, lang)}
                                </button>
                            );
                        })}
                    </div>
                </div>
            ))}
            <button onClick={onSubmit} className="w-full py-3 rounded-md bg-[#E02B2B] hover:bg-[#C92525] text-white font-semibold" data-testid="quiz-submit">{t(user, "training_submit_quiz")}</button>
        </div>
    );
}

function ResultCard({ result, staffName, onRetry, user }) {
    const pct = Math.round((result.score / result.total) * 100);
    return (
        <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-hidden" data-testid="result-card">
            <div className={`p-5 ${result.passed ? "bg-emerald-500" : "bg-[#E02B2B]"} text-white`}>
                <div className="text-[10px] font-bold uppercase tracking-widest text-white/70">{result.passed ? t(user, "training_congrats") : t(user, "training_try_again")}</div>
                <div className="font-display text-2xl font-bold flex items-center gap-2 mt-1">
                    {result.passed ? <Award className="w-6 h-6" /> : <RotateCcw className="w-6 h-6" />}
                    {result.score} / {result.total} ({pct}%)
                </div>
            </div>
            <div className="p-5">
                <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Certificate</div>
                <div className="mt-2 p-4 border-2 border-dashed border-[#1E3355]/30 rounded-lg text-center" data-testid="badge">
                    <div className="font-display text-lg font-bold text-[#1E3355]">{result.course_title}</div>
                    <div className="text-sm text-gray-700 mt-1">{staffName || result.taken_by_user_name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{fmtDate(result.created_at)} · Score {result.score}/{result.total}</div>
                </div>
                <div className="mt-4 flex gap-2">
                    <button onClick={onRetry} className="flex-1 py-2.5 rounded-md border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50" data-testid="result-retry">{t(user, "training_another")}</button>
                    <button onClick={() => window.print()} className="flex-1 py-2.5 rounded-md bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold" data-testid="result-print">Print badge</button>
                </div>
            </div>
        </div>
    );
}

const fieldCls = "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1E3355] focus:ring-1 focus:ring-[#1E3355] bg-white";
